import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { SidebarIcon } from './SidebarIcon';
import './ForgottenEmailDialog.css';

interface ForgottenEmailDialogProps {
  anchorRef: RefObject<HTMLElement>;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
  openedWithKeyboard: boolean;
}

export function ForgottenEmailDialog({ anchorRef, onClose, returnFocusRef, openedWithKeyboard }: ForgottenEmailDialogProps) {
  const [keyboardNavigation, setKeyboardNavigation] = useState(openedWithKeyboard);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const pointerStartedOutside = useRef(false);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const returnFocusElement = returnFocusRef.current;

    const { body, documentElement } = document;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };
    const previousRootOverflow = documentElement.style.overflow;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
    const bodyPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;

    // Fixing the body also prevents background scrolling on mobile Safari.
    Object.assign(body.style, {
      position: 'fixed',
      top: `${-scrollY}px`,
      left: `${-scrollX}px`,
      width: '100%',
      overflow: 'hidden',
      paddingRight: `${bodyPaddingRight + scrollbarWidth}px`,
    });
    documentElement.style.overflow = 'hidden';
    dialog.showModal();
    closeButtonRef.current?.focus();

    const positionDialog = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const cardBounds = anchor.getBoundingClientRect();
      const dialogBounds = dialog.getBoundingClientRect();
      const viewport = window.visualViewport;
      const keepVisible = (center: number, size: number, start: number, extent: number) => {
        const half = size / 2;
        if (size + 32 > extent) return start + extent / 2;
        return Math.min(start + extent - half - 16, Math.max(start + half + 16, center));
      };

      // Match the card's center; keep the existing dialog size reachable on small screens.
      dialog.style.left = `${keepVisible(
        cardBounds.left + cardBounds.width / 2,
        dialogBounds.width,
        viewport?.offsetLeft ?? 0,
        viewport?.width ?? window.innerWidth,
      )}px`;
      dialog.style.top = `${keepVisible(
        cardBounds.top + cardBounds.height / 2,
        dialogBounds.height,
        viewport?.offsetTop ?? 0,
        viewport?.height ?? window.innerHeight,
      )}px`;
    };

    positionDialog();
    const positionObserver = new ResizeObserver(positionDialog);
    if (anchorRef.current) positionObserver.observe(anchorRef.current);
    positionObserver.observe(dialog);
    window.addEventListener('resize', positionDialog);
    window.addEventListener('scroll', positionDialog, true);
    window.visualViewport?.addEventListener('resize', positionDialog);
    window.visualViewport?.addEventListener('scroll', positionDialog);

    return () => {
      positionObserver.disconnect();
      window.removeEventListener('resize', positionDialog);
      window.removeEventListener('scroll', positionDialog, true);
      window.visualViewport?.removeEventListener('resize', positionDialog);
      window.visualViewport?.removeEventListener('scroll', positionDialog);
      dialog.close();
      Object.assign(body.style, previousBodyStyles);
      documentElement.style.overflow = previousRootOverflow;
      window.scrollTo(scrollX, scrollY);
      if (returnFocusElement?.isConnected) returnFocusElement.focus({ preventScroll: true });
    };
  }, [anchorRef, returnFocusRef]);

  const isOutsideDialog = (event: { clientX: number; clientY: number }) => {
    const bounds = dialogRef.current?.getBoundingClientRect();
    return (
      !!bounds &&
      (event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom)
    );
  };

  return (
    <dialog
      ref={dialogRef}
      id="forgotten-email-dialog"
      className="forgotten-email-dialog"
      data-keyboard-navigation={keyboardNavigation ? 'true' : undefined}
      aria-modal="true"
      aria-labelledby="forgotten-email-title"
      aria-describedby="forgotten-email-description forgotten-email-phone forgotten-email-hours"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onPointerDown={(event) => {
        setKeyboardNavigation(false);
        pointerStartedOutside.current = event.target === event.currentTarget && isOutsideDialog(event);
      }}
      onClick={(event) => {
        if (pointerStartedOutside.current && event.target === event.currentTarget && isOutsideDialog(event)) onClose();
        pointerStartedOutside.current = false;
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        setKeyboardNavigation(true);
        const targets = Array.from(
          event.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
        );
        const currentIndex = targets.indexOf(document.activeElement as HTMLElement);
        const nextIndex =
          currentIndex < 0
            ? event.shiftKey
              ? targets.length - 1
              : 0
            : (currentIndex + (event.shiftKey ? -1 : 1) + targets.length) % targets.length;
        event.preventDefault();
        targets[nextIndex]?.focus();
      }}
    >
      <div className="forgotten-email-dialog__phone" aria-hidden="true">
        <SidebarIcon name="phone" className="forgotten-email-dialog__phone-icon" />
      </div>
      <h2 id="forgotten-email-title" className="forgotten-email-dialog__title text-slate-900">
        E-Mail Adresse vergessen?
      </h2>
      <p id="forgotten-email-description" className="forgotten-email-dialog__description text-slate-500">
        Bitte melden Sie sich in der Praxis:
      </p>
      <a id="forgotten-email-phone" className="forgotten-email-dialog__number" href="tel:0526254625">
        052 625 46 25
      </a>
      <p id="forgotten-email-hours" className="forgotten-email-dialog__hours text-slate-500">
        Öffnungszeiten: Mo–Fr, 8–17 Uhr
      </p>
      <button ref={closeButtonRef} type="button" className="forgotten-email-dialog__close" onClick={onClose}>
        Schliessen
      </button>
    </dialog>
  );
}
